/**
 * /help's rows about the menu's pages: the rules D1–D4 share, Result and its targets, the definitions, and D3's and
 * D4's own rule notes. Every threshold is printed from the constant its page reads and every word a page shows goes
 * through that page's own key, so a changed rule reaches /help without anyone editing it. Each function takes the
 * reader's t at render, never at import (metricHelpRegistry's rule), and holds only literal keys so the locale scan
 * sees them. Pure.
 *
 * @module components/help/helpContent
 */
import type { TFunction } from 'i18next';
import { DATE_OPTIONS } from '@/components/looker/LookerFilterBar';
import { searchShoppingNote, ruleNotesOf as audienceRuleNotesOf } from '@/services/audience/audienceText';
import { tileLabel, tileTitle } from '@/services/channels/channelFormat';
import { PLATFORM_TILES } from '@/services/channels/platformTiles';
import type { TileMetric } from '@/services/channels/channelTypes';
import { ruleNotesOf as creativeRuleNotesOf } from '@/services/creatives/creativeText';
import { DEFAULT_SHARED } from '@/services/dashboardFilters';
import { BENCHMARK_MIN_IMPRESSIONS } from '@/services/insights/insightConstants';
import { RESULT_LAG_DAYS } from '@/services/looker/lookerDateRange';
import { directionOf, type MetricDirection } from '@/services/looker/lookerDelta';
import { FB_CONVERSION_ACTION_TYPES } from '@/services/mediaApi/facebookActionTypes';
import { TIKTOK_CONVERSIONS } from '@/services/mediaApi/fieldMapping';
import { costPerResultLabel, DASH, fmtCount, objectiveText, resultLabel } from '@/services/overview/overviewFormat';
import { ALLOWED_METRICS } from '@/services/targets/resultMetric';
import { NEAR_LIMIT, TARGET_OBJECTIVES } from '@/services/targets/targetRules';
import type { HelpAnchor } from './metricHelpRegistry';

/** One card: a term and how it is arrived at. A formula starting with "—" is an absence. */
export interface HelpTerm {
  /** Only the cards a "?" links to carry one; written out, never derived from `term`, so a renamed label keeps its link. */
  readonly id?: HelpAnchor;
  readonly term: string;
  readonly formula?: string;
  readonly lines?: readonly string[];
  readonly note?: string;
}

export interface PageRules {
  readonly page: string;
  readonly notes: readonly string[];
}

const presetLabel = (value: string, t: TFunction): string => DATE_OPTIONS.find((o) => o.value === value)?.label(t) ?? value;

/** The pages these rules hold on, by their menu names. */
export const sharedPagesOf = (t: TFunction): string =>
  [t('nav.executiveOverview'), t('nav.channelDeepDive'), t('nav.creativePerformance'), t('nav.audienceTargeting')].join(', ');

/** Figures whose Δ the pages colour, by the key lookerDelta's map reads, each in the word its page prints. */
const DELTA_FIGURES: ReadonlyArray<readonly [string, (t: TFunction) => string]> = [
  ['result', (t) => t('overview.result')],
  ['cost_per_result', (t) => t('overview.costPerResult')],
  ['ctr', (t) => t('overview.metric.ctr')],
  ['cpc', (t) => t('overview.metric.cpc')],
  ['cpm', (t) => t('overview.metric.cpm')],
  ['conversions', (t) => t('channels.tile.conversions')],
  ['cpa', (t) => t('channels.tile.cpa')],
  ['cpl', (t) => t('channels.tile.cpl')],
  ['roas', (t) => t('channels.tile.roas')],
  ['spend', (t) => t('overview.metric.spend')],
  ['impressions', (t) => t('overview.metric.impressions')],
  ['clicks', (t) => t('overview.metric.clicks')],
];

const deltaLines = (t: TFunction): string[] => {
  const list = (d: MetricDirection): string =>
    DELTA_FIGURES.filter(([key]) => directionOf(key) === d).map(([, label]) => label(t)).join(', ');
  return [
    t('help.rules.delta.up', { list: list('up') }),
    t('help.rules.delta.down', { list: list('down') }),
    t('help.rules.delta.neutral', { list: list('neutral') }),
  ];
};

/** D2's tiles counted through the cut, in its registry's order. */
const cutTilesOf = (t: TFunction): string =>
  [...new Set(Object.values(PLATFORM_TILES).flat().filter((s) => s.cut).map((s) => s.metric))]
    .map((m) => tileLabel(m, t)).join(', ');

export const rulesOf = (t: TFunction): HelpTerm[] => [
  {
    term: t('overview.filters.period'),
    formula: t('help.rules.period.formula', { preset: presetLabel(DEFAULT_SHARED.period, t) }),
    note: t('help.rules.period.note', { mtd: presetLabel('mtd', t), lastMonth: presetLabel('last_month', t) }),
  },
  {
    term: t('help.rules.delta.term'),
    formula: t('help.rules.delta.formula'),
    lines: deltaLines(t),
    note: t('help.rules.delta.note', { dash: DASH }),
  },
  {
    term: t('overview.cutChip'),
    formula: t('help.rules.cut.formula', { days: RESULT_LAG_DAYS }),
    // The chip counts today, which no period includes.
    note: t('help.rules.cut.note', { lost: RESULT_LAG_DAYS - 1, channels: t('nav.channelDeepDive'), tiles: cutTilesOf(t) }),
  },
  {
    term: t('help.rules.absent.term', { notReported: t('overview.notReported'), dash: DASH }),
    lines: [
      t('help.rules.absent.notReported', { word: t('overview.notReported') }),
      t('help.rules.absent.dash', { word: DASH }),
    ],
    note: t('help.rules.absent.note'),
  },
  {
    term: t('overview.lowSample'),
    formula: t('help.rules.lowSample.formula', { n: fmtCount(BENCHMARK_MIN_IMPRESSIONS) }),
    note: t('help.rules.lowSample.note'),
  },
  {
    term: t('overview.col.currency'),
    formula: t('help.rules.currency.formula'),
    note: t('help.rules.currency.note', { unknown: t('looker.currency.unknownExcluded'), budget: t('nav.budgetMmm') }),
  },
];

/** Each objective's Result and cost, as box 3 labels them; retention and unspecified have none. */
const objectiveRows = (t: TFunction): HelpTerm[] => [
  ...TARGET_OBJECTIVES.map((o) => ({
    term: objectiveText(o, t),
    lines: ALLOWED_METRICS[o].map((m) => `${resultLabel(m, t)} · ${costPerResultLabel(m, t)}`),
  })),
  { term: [objectiveText('retention', t), objectiveText('unspecified', t)].join(' · '), formula: t('help.results.noResult') },
];

/** targetVerdict's bands and the cells' other states, in the target cells' own words. */
const targetLines = (t: TFunction): string[] => {
  const line = (when: string, status: string): string => t('help.results.targets.band', { when, status });
  return [
    line('≤ 1', t('looker.targets.status.met')),
    line(`≤ ${NEAR_LIMIT}`, t('looker.targets.status.near')),
    line(`> ${NEAR_LIMIT}`, t('looker.targets.status.over25')),
    line(t('help.results.targets.noTarget'), t('looker.targets.status.none')),
    line(t('help.results.targets.na'), t('looker.targets.status.na')),
    line(t('help.results.targets.failed'), t('looker.targets.status.failed')),
    line(t('help.results.targets.noCurrency'), t('looker.targets.status.noCurrency')),
    line(t('help.results.targets.otherMetric'), t('looker.targets.status.otherMetric')),
  ];
};

export const resultsOf = (t: TFunction): HelpTerm[] => [
  ...objectiveRows(t),
  {
    term: t('overview.result'),
    formula: t('help.results.result.formula'),
    note: t('help.results.result.note', {
      badge: t('looker.resultMapping.badge'), caveat: t('looker.resultMapping.caveat'), failed: t('looker.resultMapping.failed'),
      overview: t('nav.executiveOverview'), creatives: t('nav.creativePerformance'), dash: DASH,
    }),
  },
  {
    term: t('overview.costPerResult'),
    formula: t('help.results.cost.formula'),
    note: t('help.results.cost.note', { dash: DASH, notReported: t('overview.notReported') }),
  },
  {
    term: t('looker.targets.column'),
    formula: t('help.results.targets.formula'),
    lines: targetLines(t),
    note: t('help.results.targets.note', { met: t('looker.targets.status.met'), over: t('looker.targets.status.over25') }),
  },
  {
    term: t('help.results.where.term'),
    lines: [
      t('help.results.where.overview', {
        page: t('nav.executiveOverview'), box3: t('overview.box.result'), box9: t('overview.box.objectives'), box13: t('overview.box.campaigns'),
      }),
      t('help.results.where.creatives', { page: t('nav.creativePerformance'), result: t('creatives.col.result'), cost: t('creatives.col.costPerResult') }),
      t('help.results.where.budget', {
        page: t('nav.budgetMmm'), interim: t('budget.interim.title'), cpr: t('budget.allocation.col.cpr'), allocation: t('budget.allocation.title'),
      }),
      t('help.results.where.none', { channels: t('nav.channelDeepDive'), audience: t('nav.audienceTargeting') }),
    ],
  },
];

export const definitionsOf = (t: TFunction): HelpTerm[] => {
  // D2 names the objective CPA, CPL and ROAS read: under "all" objectives, Conversion.
  const conversionTile = (metric: TileMetric): string => tileTitle({ metric, objective: 'conversion' }, t);
  const outcomes = { box: t('overview.box.outcomes'), page: t('nav.executiveOverview') };
  const lead = t('budget.unit.lead');
  return [
    {
      id: 'def-conversions', term: t('channels.tile.conversions'),
      formula: t('help.def.conversions.formula'),
      note: t('help.def.conversions.note', {
        fb: FB_CONVERSION_ACTION_TYPES.size, tiktok: TIKTOK_CONVERSIONS.length,
        days: RESULT_LAG_DAYS, cpr: t('budget.allocation.col.cpr'), allocation: t('budget.allocation.title'), budget: t('nav.budgetMmm'),
      }),
    },
    {
      id: 'def-cpl', term: `${t('channels.tile.cpl')} / ${t('looker.app.table.costPerLead')}`,
      formula: t('help.def.cpl.formula'),
      lines: [
        t('help.def.cpl.platform', { page: t('nav.channelDeepDive'), tile: conversionTile('cpl') }),
        t('help.def.cpl.imported', { ...outcomes, label: t('looker.app.table.costPerLead') }),
        t('help.def.cpl.model', {
          avgCost: t('budget.contribution.col.avgCost', { unit: lead }), nextCost: t('budget.contribution.col.nextCost', { unit: lead }),
          page: t('nav.budgetMmm'),
        }),
      ],
      note: t('help.def.cpl.note', { dash: DASH }),
    },
    {
      id: 'def-cpa', term: t('help.def.cpa.term'),
      formula: t('help.def.cpa.formula'),
      note: t('help.def.cpa.note', {
        channels: t('nav.channelDeepDive'), tile: conversionTile('cpa'), cost: t('overview.costPerResult'),
        overview: t('nav.executiveOverview'), creatives: t('nav.creativePerformance'),
      }),
    },
    {
      id: 'def-cpc', term: t('overview.metric.cpc'),
      formula: t('help.def.cpc.formula'),
      note: t('help.def.cpc.note', { cost: t('overview.costPerResult') }),
    },
    { id: 'def-ctr', term: t('overview.metric.ctr'), formula: t('help.def.ctr.formula') },
    { id: 'def-cvr', term: t('help.def.cvr.term'), formula: t('help.def.cvr.formula'), note: t('help.def.cvr.note', { pages: [sharedPagesOf(t), t('nav.budgetMmm')].join(', ') }) },
    { id: 'def-cpm', term: t('overview.metric.cpm'), formula: t('help.def.cpm.formula') },
    {
      id: 'def-roas', term: t('channels.tile.roas'),
      formula: t('help.def.roas.formula'),
      lines: [
        t('help.def.roas.platform', { page: t('nav.channelDeepDive'), tile: conversionTile('roas'), notReported: t('overview.notReported') }),
        t('help.def.roas.media', { ...outcomes, label: t('looker.app.table.mediaRoas') }),
        t('help.def.roas.model', { roi: t('budget.contribution.col.roi'), simRoas: t('budget.simulator.kpi.roas'), page: t('nav.budgetMmm') }),
      ],
      note: t('help.def.roas.note'),
    },
    {
      id: 'def-leads', term: t('channels.tile.leads'),
      lines: [
        t('help.def.leads.platform', { page: t('nav.channelDeepDive') }),
        t('help.def.leads.imported', {
          leads: t('looker.app.table.leads'), box: outcomes.box, overview: outcomes.page,
          metric: t('budget.metric.leads'), budget: t('nav.budgetMmm'),
        }),
      ],
      note: t('help.def.leads.note', { notReported: t('overview.notReported') }),
    },
    {
      id: 'def-lead-to-sale', term: t('help.def.leadToSale.term'),
      formula: t('help.def.leadToSale.formula'),
      note: t('help.def.leadToSale.note', outcomes),
    },
  ];
};

/** The rules D3 and D4 print under their tables, quoted through their own functions. */
export const pageRulesOf = (t: TFunction): PageRules[] => [
  { page: t('nav.creativePerformance'), notes: creativeRuleNotesOf(t) },
  {
    page: t('nav.audienceTargeting'),
    notes: [...audienceRuleNotesOf(t), searchShoppingNote(null, t), t('audience.notes.later')],
  },
];
